// shop-frontend/src/context/ShopNotificationsContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getShopNotifications,
  markShopNotificationRead,
  markAllShopNotificationsRead,
  deleteShopNotification,
  deleteAllShopNotifications,
} from "../utils/api";
import { useShopAuth } from "./ShopAuthContext";

const ShopNotificationsContext = createContext(null);

export function ShopNotificationsProvider({ children }) {
  const { shop, token } = useShopAuth(); // shop truthy = logged in, token for auth state
  const [items, setItems] = useState([]);
  const [serverUnreadCount, setServerUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // interval + safety
  const intervalRef = useRef(null);
  const aliveRef = useRef(true);
  const stopPollingRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const unreadCount = useMemo(() => {
    const fallback = (items || []).filter((n) => !n.readAt && !n.read).length;
    const n = Number(serverUnreadCount);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }, [items, serverUnreadCount]);

  const refresh = useCallback(async () => {
    // if logged out or polling stopped, don't fetch
    if (!shop || stopPollingRef.current) return;

    try {
      setLoading(true);
      const res = await getShopNotifications();

      const list =
        (Array.isArray(res) && res) ||
        res?.notifications ||
        res?.data?.notifications ||
        res?.data ||
        [];

      if (!aliveRef.current) return;
      setItems(Array.isArray(list) ? list : []);
      setServerUnreadCount(Number(res?.unreadCount || 0));
    } catch (e) {
      // Your utils/api "request" style typically throws: err.status
      const status = e?.status || e?.response?.status || null;
      const msg = String(e?.message || "").toLowerCase();

      const unauthorized =
        status === 401 ||
        status === 403 ||
        msg.includes("unauthorized") ||
        msg.includes("jwt") ||
        msg.includes("token") ||
        msg.includes("401") ||
        msg.includes("403");

      if (unauthorized) {
        console.warn("Shop notifications: Unauthorized. Stopping polling.");
        stopPollingRef.current = true;

        // Optional: clear list (so badge disappears)
        if (aliveRef.current) setItems([]);
        if (aliveRef.current) setServerUnreadCount(0);
        return;
      }

      console.log("Shop notifications refresh error:", e?.message || e);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [shop]);

  const markOneRead = useCallback(
    async (id) => {
      const targetId = String(id || "").trim();
      if (!targetId) return;

      // optimistic UI
      setItems((prev) =>
        (prev || []).map((n) => {
          const nid = String(n._id || n.id || "").trim();
          if (nid !== targetId) return n;
          return { ...n, readAt: n.readAt || new Date().toISOString(), read: true };
        })
      );
      setServerUnreadCount((c) => Math.max(0, Number(c || 0) - 1));

      try {
        await markShopNotificationRead(targetId);
      } catch (e) {
        console.log("markOneRead error:", e?.message || e);
        refresh();
      }
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    // optimistic UI
    setItems((prev) =>
      (prev || []).map((n) => ({
        ...n,
        readAt: n.readAt || new Date().toISOString(),
        read: true,
      }))
    );
    setServerUnreadCount(0);

    try {
      await markAllShopNotificationsRead();
    } catch (e) {
      console.log("markAllRead error:", e?.message || e);
      refresh();
    }
  }, [refresh]);

  const deleteOne = useCallback(async (id) => {
    const targetId = String(id || "").trim();
    if (!targetId) return;

    // check if unread before removing
    const wasUnread = (items || []).some(
      (n) => String(n._id || n.id || "") === targetId && !n.readAt && !n.read
    );

    setItems((prev) => (prev || []).filter((n) => String(n._id || n.id || "") !== targetId));
    if (wasUnread) setServerUnreadCount((c) => Math.max(0, c - 1));

    try {
      await deleteShopNotification(targetId);
    } catch (e) {
      console.log("deleteOne error:", e?.message || e);
      refresh();
    }
  }, [items, refresh]);

  const deleteAll = useCallback(async () => {
    setItems([]);
    setServerUnreadCount(0);
    try {
      await deleteAllShopNotifications();
    } catch (e) {
      console.log("deleteAll error:", e?.message || e);
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    // Whenever auth changes:
    // - if logged out: clear + stop
    // - if logged in: start interval once
    stopPollingRef.current = false;

    // Clear any old interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!shop || !token) {
      setItems([]);
      setServerUnreadCount(0);
      setLoading(false);
      return;
    }

    // initial fetch
    refresh();

    // poll every 15s
    intervalRef.current = setInterval(() => {
      if (!stopPollingRef.current) refresh();
    }, 15000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [shop, token, refresh]);

  const value = useMemo(
    () => ({
      items,
      loading,
      unreadCount,
      refresh,
      markOneRead,
      markAllRead,
      deleteOne,
      deleteAll,
    }),
    [items, loading, unreadCount, refresh, markOneRead, markAllRead, deleteOne, deleteAll]
  );

  return (
    <ShopNotificationsContext.Provider value={value}>
      {children}
    </ShopNotificationsContext.Provider>
  );
}

export function useShopNotifications() {
  const ctx = useContext(ShopNotificationsContext);
  if (!ctx) {
    throw new Error("useShopNotifications must be used inside ShopNotificationsProvider");
  }
  return ctx;
}
